"use client";

import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../lib/firebase";
import RequireAuth from "../components/RequireAuth";
import Header from "../components/Header";
import { getProfileAsync } from "../lib/storage";
import * as firebaseStorage from "../lib/firebase-storage";
import { uploadChatImage } from "../lib/firebase-storage";
import { showToast } from "../lib/toast";
import { ChatListSkeleton, ChatMessageSkeleton } from "../components/LoadingSkeleton";
import Avatar from "../components/Avatar";

type Msg = {
  id: string;
  from: string;
  text: string;
  createdAt: number;
  delivered?: boolean;
  read?: boolean;
  imageUrl?: string;
};

// Cache for user profiles to avoid repeated lookups
const profileCache: Record<string, string> = {};

function getUser() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("nosiva_user");
}

// Function to check if message is an offer message
function isOfferMessage(text: string): boolean {
  return text.startsWith("Ponuda za");
}

// Function to parse offer message
function parseOfferMessage(text: string): { title: string; amount: string; listingId: string | null } | null {
  if (!isOfferMessage(text)) return null;
  
  // Format: "Ponuda za "title":\namount KM\n\nurl"
  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length < 2) return null;
  
  // Extract title from first line: "Ponuda za "title":"
  const titleMatch = lines[0].match(/Ponuda za "([^"]+)":/);
  if (!titleMatch) return null;
  const title = titleMatch[1];
  
  // Extract amount from second line: "amount KM"
  const amountMatch = lines[1].match(/([\d,\.]+)\s*KM/);
  if (!amountMatch) return null;
  const amount = amountMatch[1];
  
  // Extract listing ID from URL (last line)
  const urlLine = lines[lines.length - 1];
  const marketplaceMatch = urlLine.match(/marketplace\/([a-zA-Z0-9]+)/);
  const listingId = marketplaceMatch ? marketplaceMatch[1] : null;
  
  return { title, amount, listingId };
}

// Function to render offer message as a card
function renderOfferMessage(text: string, router: any, isOwn: boolean) {
  const offer = parseOfferMessage(text);
  if (!offer) return null;
  
  return (
    <div className={`rounded-lg border ${isOwn ? 'border-white/20 bg-white/10' : 'border-[var(--border)] bg-[var(--card)]'} p-3 space-y-2`}>
      <div className="flex items-center gap-2">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={isOwn ? "text-white/80" : "text-[var(--accent)]"}>
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
        <span className={`text-xs font-semibold ${isOwn ? "text-white/90" : "text-[var(--accent)]"}`}>
          Ponuda
        </span>
      </div>
      <div className={`text-sm font-medium ${isOwn ? "text-white" : "text-[var(--text)]"}`}>
        {offer.title}
      </div>
      <div className={`text-lg font-bold ${isOwn ? "text-white" : "text-[var(--accent)]"}`}>
        {offer.amount} KM
      </div>
      {offer.listingId && (
        <a
          href={`/listing/${offer.listingId}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            router.push(`/listing/${offer.listingId}`);
          }}
          className={`inline-flex items-center gap-1.5 text-xs font-medium hover:opacity-80 transition ${
            isOwn ? "text-white/90 underline" : "text-[var(--accent)] underline"
          }`}
        >
          <span>Pogledaj oglas</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        </a>
      )}
    </div>
  );
}

// Function to render text with clickable links
function renderMessageText(text: string, router: any) {
  // Match URLs (http, https, or localhost with path)
  const urlRegex = /(https?:\/\/[^\s]+|localhost:\d+[^\s]*)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  
  // Reset regex
  urlRegex.lastIndex = 0;
  
  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex, match.index)}</span>);
    }
    
    const url = match[0];
    // Check if it's a listing link
    const listingMatch = url.match(/listing\/([a-zA-Z0-9]+)/);
    if (listingMatch) {
      const listingId = listingMatch[1];
      parts.push(
        <a
          key={`link-${match.index}`}
          href={`/listing/${listingId}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            router.push(`/listing/${listingId}`);
          }}
          className="underline hover:opacity-80 transition text-inherit font-medium"
        >
          {url}
        </a>
      );
    } else {
      // Regular link
      parts.push(
        <a
          key={`link-${match.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="underline hover:opacity-80 transition text-inherit"
        >
          {url}
        </a>
      );
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>);
  }
  
  return parts.length > 0 ? parts : <span>{text}</span>;
}

function getThreadKey(
  currentUser?: string | null,
  otherUser?: string | null,
  listing?: string | null
) {
  const participants = [currentUser, otherUser]
    .filter(Boolean)
    .map((x) => String(x))
    .sort();

  const base = participants.length ? participants.join("__") : "general";
  const suffix = listing ? `_${listing}` : "";

  return `nosiva_chat_${base}${suffix}`;
}

type Conversation = {
  key: string;
  otherLabel: string;
  listingLabel?: string;
  lastTs: number;
  lastText?: string;
  unreadCount?: number;
  avatarUrl?: string;
  thread?: {
    title?: string;
    participantNames?: Record<string, string>;
    participantAvatars?: Record<string, string>;
    participants: string[];
  };
};

export default function ChatPageClient() {
  const router = useRouter();
  const params = useSearchParams();
  const withUser = params.get("with");
  const withUserId = params.get("withId");
  const shareText = params.get("shareText");

  const [user, setUser] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [threads, setThreads] = useState<Conversation[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string>("");
  const [isMobile, setIsMobile] = useState(false);
  const [showList, setShowList] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [lastMessageDoc, setLastMessageDoc] = useState<any>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [showNewMessagesPill, setShowNewMessagesPill] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [headerHeight, setHeaderHeight] = useState<number>(56);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [pendingMsgs, setPendingMsgs] = useState<Msg[]>([]);

  const combinedMsgs = useMemo(() => {
    // Oldest first, newest (including pending) at the bottom
    return [...msgs].reverse().concat(pendingMsgs);
  }, [msgs, pendingMsgs]);

  const key = useMemo(() => {
    // ALWAYS use withUserId (Firebase UID) for thread key – names are not reliable
    // If we don't have withUserId yet, we *must not* create a thread key, otherwise
    // we'd create a one-sided chat that only sadrži trenutnog korisnika.
    if (!user) return null;
    if (!withUserId) return null;
    // Always use a single thread per pair of users (no listing suffix),
    // so offers/messages don't create multiple separate chats.
    return getThreadKey(user, withUserId, null);
  }, [user, withUserId]);

  useEffect(() => {
    // Subscribe to auth state to get Firebase user ID
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        const u = getUser();
        setUser(u);
        return;
      }

      const userId = firebaseUser.uid;
      setUser(userId);
      
      // Load current user profile
      getProfileAsync(userId).then(async (p) => {
        const { getDisplayName } = await import("../lib/firebase-storage");
        const displayName = getDisplayName(p, userId);
        setProfileName(displayName);
        profileCache[userId] = displayName;
      }).catch(console.error);
    });

    return () => unsubscribeAuth();
  }, []);

  // Safety: hide any stray "Početak" buttons that might be rendered outside our React tree (old code, extensions, etc.)
  useEffect(() => {
    if (typeof document === "undefined") return;

    const hidePocetakButtons = () => {
      const buttons = Array.from(document.querySelectorAll("button"));
      for (const btn of buttons) {
        const text = btn.textContent?.trim();
        if (text === "Početak") {
          (btn as HTMLButtonElement).style.display = "none";
        }
      }
    };

    hidePocetakButtons();

    const observer = new MutationObserver(() => hidePocetakButtons());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Set active key when key changes (from URL params)
  // Only set if activeKey is not already set (to avoid overriding user's click)
  useEffect(() => {
    if (key && user && !activeKey) {
      // Only set activeKey from URL if it's not already set
      // This allows user clicks to set activeKey first
      setActiveKey(key);
    } else if (!key && !activeKey) {
      setActiveKey(null);
    }
  }, [key, user]);

  // Set share text when shareText param is present and auto-send it
  useEffect(() => {
    if (shareText && key && user) {
      const decodedText = decodeURIComponent(shareText);
      setText(decodedText);
      
      // Auto-send the message immediately
      const sendShareMessage = async () => {
        try {
          const newMsg: any = {
            from: user,
            text: decodedText.trim(),
            createdAt: Date.now(),
            delivered: false,
            read: false,
          };
          
          await firebaseStorage.sendChatMessage(key, newMsg);
          setText("");
          
          // Clean up URL by removing shareText param
          const newParams = new URLSearchParams(params.toString());
          newParams.delete("shareText");
          const newUrl = `/chat?${newParams.toString()}`;
          router.replace(newUrl);
        } catch (error: any) {
          console.error("Error auto-sending share message:", error);
          // If auto-send fails, just set the text so user can send manually
          setText(decodedText);
        }
      };
      
      sendShareMessage();
    }
  }, [shareText, key, user, params, router]);

  // Reset scroll state when thread changes (via URL or sidebar click)
  useEffect(() => {
    didInitialScroll.current = false;
    setShowNewMessagesPill(false);
  }, [key, activeKey]);

  useEffect(() => {
    // Use activeKey if available, otherwise use key from URL params
    // Prioritize activeKey because it's set immediately when user clicks on a thread
    const threadKeyToUse = activeKey || key;
    
    if (!user || !threadKeyToUse) {
      setMsgs([]);
      setHasMoreMessages(false);
      setLastMessageDoc(null);
      return;
    }

    let isMounted = true;

    // Clear messages first to show loading state
    if (isMounted) {
      setMsgs([]);
      setHasMoreMessages(false);
      setLastMessageDoc(null);
    }

    // Load messages from Firebase (DESC order, newest first)
    firebaseStorage.getChatMessages(threadKeyToUse).then((result) => {
      if (isMounted) {
        setMsgs(result.messages);
        setHasMoreMessages(result.hasMore);
        setLastMessageDoc(result.lastDoc);
      }
    }).catch((error) => {
      console.error("Error loading messages:", error);
      if (isMounted) {
        setMsgs([]);
        setHasMoreMessages(false);
        setLastMessageDoc(null);
      }
    });
    
    // Subscribe to real-time updates
    const unsubscribeMessages = firebaseStorage.subscribeToChatMessages(threadKeyToUse, user || "", (messages) => {
      if (!isMounted) return;
      
      // Filter out messages deleted for current user
      const filtered = messages.filter((m) => !m.deletedFor?.[user || ""]);
      setMsgs(filtered);
      
      // Check if user is near bottom to auto-scroll or show "new messages" pill
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50;

        if (isNearBottom) {
          // Auto-scroll to bottom (newest messages)
          requestAnimationFrame(() => {
            if (messagesContainerRef.current && isMounted) {
              // Use the same robust helper that works well on mobile browsers
              const containerEl = messagesContainerRef.current;
              if (containerEl) {
                containerEl.scrollTop = containerEl.scrollHeight;
              }
            }
            setShowNewMessagesPill(false);
          });
        } else {
          // Show "new messages" pill
          setShowNewMessagesPill(true);
        }
      }
    });

    // Mark thread as read when user views it
    firebaseStorage.markThreadAsRead(threadKeyToUse).catch(() => {});

    return () => {
      isMounted = false;
      unsubscribeMessages();
    };
  }, [user, activeKey, key]);

  // Auto-scroll to bottom on initial load and when thread changes - newest messages at bottom
  useEffect(() => {
    const threadKeyForScroll = activeKey || key;

    if (!msgs.length || !threadKeyForScroll) return;

    // On mobile, wait until the messages panel is actually visible
    if (isMobile && showList) return;

    // Only auto-scroll if we haven't done it for this thread yet
    if (didInitialScroll.current) return;

    // For mobile browsers (especially iOS Safari), the viewport height can change
    // for a short time after opening the screen (URL bar collapsing, etc.).
    // To keep the latest message pinned to the bottom, we nudge the scroll
    // for a brief period until things stabilize, but only when the container
    // size actually changes to avoid unnecessary reflows.
    const start = performance.now();
    let lastScrollHeight = 0;
    let lastClientHeight = 0;

    const tick = () => {
      if (didInitialScroll.current) return;

      const container = messagesContainerRef.current;
      if (!container) return;

      const currentScrollHeight = container.scrollHeight;
      const currentClientHeight = container.clientHeight;

      // Only snap to bottom if the container size changed since last frame
      if (
        currentScrollHeight !== lastScrollHeight ||
        currentClientHeight !== lastClientHeight
      ) {
        lastScrollHeight = currentScrollHeight;
        lastClientHeight = currentClientHeight;
        scrollToBottom({
          scrollHeight: currentScrollHeight,
          clientHeight: currentClientHeight,
        });
      }

      // If we've been doing this for a short stabilization window, stop and mark as done
      if (performance.now() - start > 400) {
        didInitialScroll.current = true;
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);

    return () => {
      didInitialScroll.current = false;
    };
  }, [key, activeKey, msgs.length, isMobile, showList]);

  // Load older messages when scrolling to top (older messages are at top)
  // Throttled to improve performance
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Throttle scroll handler to reduce lag (only for loading older messages)
    scrollTimeoutRef.current = setTimeout(() => {
      // Use the same thread key as the main loader (activeKey has priority)
      const threadKeyToUse = activeKey || key;
      if (!messagesContainerRef.current || isLoadingOlder || !hasMoreMessages || !threadKeyToUse) return;
      
      const currentScrollTop = messagesContainerRef.current.scrollTop;
      // If scrolled near top (within 100px), load older messages
      if (currentScrollTop < 100) {
        setIsLoadingOlder(true);
        firebaseStorage.getChatMessages(threadKeyToUse, lastMessageDoc).then((result) => {
          // Add older messages to the beginning (they're in DESC order, so we reverse and prepend)
          const olderMessages = [...result.messages].reverse();
          setMsgs((prev) => [...olderMessages, ...prev]);
          setHasMoreMessages(result.hasMore);
          setLastMessageDoc(result.lastDoc);
          // Maintain scroll position after adding older messages
          const scrollHeightBefore = messagesContainerRef.current?.scrollHeight || 0;
          requestAnimationFrame(() => {
            if (messagesContainerRef.current) {
              const scrollHeightAfter = messagesContainerRef.current.scrollHeight;
              messagesContainerRef.current.scrollTop = scrollHeightAfter - scrollHeightBefore + currentScrollTop;
            }
          });
          setIsLoadingOlder(false);
        }).catch((error) => {
          console.error("Error loading older messages:", error);
          setIsLoadingOlder(false);
        });
      }
    }, 150); // Throttle to 150ms
  }, [activeKey, key, isLoadingOlder, hasMoreMessages, lastMessageDoc]);
  
  // Function to scroll to bottom (newest messages)
  const scrollToBottom = (
    dimensions?: { scrollHeight: number; clientHeight: number }
  ) => {
    const container = messagesContainerRef.current;

    // Also scroll the full page on mobile so the chat area itself is in view.
    if (typeof window !== "undefined" && isMobile) {
      const doc = document.scrollingElement || document.documentElement;
      doc.scrollTop = doc.scrollHeight;
    }

    if (!container) return;

    const scrollHeight =
      dimensions?.scrollHeight ?? container.scrollHeight;
    const clientHeight =
      dimensions?.clientHeight ?? container.clientHeight;

    const maxScrollTop = scrollHeight - clientHeight;
    container.scrollTop = maxScrollTop > 0 ? maxScrollTop + 4 : 0;
  };

  // Mark thread as read ONLY when user clicks on it in the list
  // Badge stays until user actively opens the thread

  useEffect(() => {
    if (!user) {
      setThreads([]);
      return;
    }

    // Load conversations from Firebase with real-time updates
    let isLoadingThreads = true;
    const unsubscribeThreads = firebaseStorage.subscribeToChatThreads(user, async (firebaseThreads) => {
      isLoadingThreads = false;
      // Load profiles for participants that don't have names in participantNames
      const allParticipantIds = new Set<string>();
      firebaseThreads.forEach(thread => {
        thread.participants.forEach(p => {
          if (p && p !== user) {
            // Only load if not in participantNames
            if (!thread.participantNames?.[p]) {
              allParticipantIds.add(p);
            } else {
              // Cache the name from participantNames
              profileCache[p] = thread.participantNames[p];
            }
          }
        });
      });

      // Load all missing profiles
      const profilePromises = Array.from(allParticipantIds)
        .filter(p => !profileCache[p])
        .map(async (p) => {
          try {
            const profile = await getProfileAsync(p);
            const { getDisplayName } = await import("../lib/firebase-storage");
            const displayName = getDisplayName(profile, p);
            profileCache[p] = displayName;
          } catch (error) {
            // Ignore errors, use fallback
            profileCache[p] = `Korisnik ${p.substring(0, 8)}...`;
          }
        });

      await Promise.all(profilePromises);

      // Create conversations with proper names and unread counts
      const convos: Conversation[] = firebaseThreads.map((thread) => {
        const participants = thread.participants || [];
        let otherId = participants.find((p) => p !== user) || "";

        // Fallback: ako participants ne sadrži drugog usera (stari threadovi),
        // pokušaj da ga izvučeš iz threadKey-a (nosiva_chat_user1__user2[_listing])
        if (!otherId && thread.threadKey?.startsWith("nosiva_chat_") && user) {
          const rest = thread.threadKey.replace("nosiva_chat_", "");
          const lastDoubleUnderscore = rest.lastIndexOf("__");
          let participantsPart = rest;
          if (lastDoubleUnderscore > 0) {
            const afterLastDoubleUnderscore = rest.substring(lastDoubleUnderscore + 2);
            if (afterLastDoubleUnderscore.includes("_")) {
              // ima listing suffix, odreži ga
              participantsPart = rest.substring(0, lastDoubleUnderscore + 2);
            }
          }
          const parsedParticipants = participantsPart.split("__").filter(Boolean);
          otherId = parsedParticipants.find((p) => p !== user) || "";
        }
        
        // Get thread title: title (for group) or name of other person (for 1-on-1)
        let threadTitle = "Chat";
        if (thread.title) {
          // Group chat with title
          threadTitle = thread.title;
        } else if (otherId) {
          // 1-on-1 chat: use participant name
          threadTitle = thread.participantNames?.[otherId] 
            || profileCache[otherId] 
            || `Korisnik ${otherId.substring(0, 8)}...`;
        }

        // Use unread count from thread (already loaded from chats doc)
        const unreadCount = thread.unread?.[user] || 0;

        // Get avatar for other user
        const avatarUrl = otherId ? (thread.participantAvatars?.[otherId] || "") : "";

        return {
          key: thread.threadKey,
          otherLabel: threadTitle,
          listingLabel: undefined,
          lastTs: thread.lastUpdated ?? 0,
          lastText: String(thread.lastMessageText || ""),
          unreadCount,
          avatarUrl,
          thread: {
            title: thread.title,
            participantNames: thread.participantNames,
            participantAvatars: thread.participantAvatars,
            participants: thread.participants,
          },
        } as Conversation;
      });
      
      // Sort by last message time (newest first)
      convos.sort((a, b) => (b.lastTs || 0) - (a.lastTs || 0));
      setThreads(convos);
    });

    return () => unsubscribeThreads();
  }, [user]);

  // Track viewport to switch layouts
  useEffect(() => {
    function update() {
      const mobile = typeof window !== "undefined" && window.innerWidth < 768;
      setIsMobile(mobile);
      // On mobile, show list by default if no active chat
      if (mobile) {
        setShowList(!activeKey);
      } else {
        setShowList(true);
      }
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [activeKey]);

  // Auto-show list on mobile when activeKey is cleared
  useEffect(() => {
    if (isMobile && !activeKey) {
      setShowList(true);
    }
  }, [isMobile, activeKey]);

  // Measure header height so the chat header can stick exactly below it
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateHeaderHeight = () => {
      const headerEl = document.querySelector("header");
      if (headerEl) {
        const rect = headerEl.getBoundingClientRect();
        // Fallback to previous value if measurement fails
        if (rect.height > 0) {
          setHeaderHeight(rect.height);
        }
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  async function send() {
    if (!user) {
      router.push("/login");
      return;
    }
    const trimmedText = text.trim();
    if (!trimmedText && !selectedImage) return;

    const threadKeyToUse = activeKey || key;
    if (!threadKeyToUse) {
      showToast.error("Nije moguće poslati poruku. Molimo osvježite stranicu.");
      return;
    }

    // Snapshot trenutnog stanja prije slanja (za optimistični prikaz)
    const currentImageFile = selectedImage;
    const currentImagePreview = imagePreview;
    const optimisticId = `pending-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;

    const optimisticMsg: Msg = {
      id: optimisticId,
      from: user,
      text: trimmedText || "",
      createdAt: Date.now(),
      delivered: false,
      read: false,
      imageUrl: currentImagePreview || undefined,
    };

    // Dodajmo poruku odmah u UI kao "šalje se..."
    setPendingMsgs((prev) => [...prev, optimisticMsg]);
    setText("");
    setSelectedImage(null);
    setImagePreview(null);
    // Reset file input odmah da se osjeti responsivnost
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    let imageUrl: string | undefined = undefined;

    // Upload image ako je bilo odabrane slike
    if (currentImageFile) {
      try {
        setIsUploadingImage(true);
        imageUrl = await uploadChatImage(currentImageFile, threadKeyToUse, user);
      } catch (error: any) {
        console.error("Error uploading image:", error);
        showToast.error(error?.message || "Greška pri učitavanju slike.");
        setIsUploadingImage(false);
        // Ukloni optimističnu poruku i vrati tekst da korisnik može pokušati ponovo
        setPendingMsgs((prev) => prev.filter((m) => m.id !== optimisticId));
        setText(trimmedText);
        return;
      }
    }

    // Kreiraj stvarnu poruku za backend (bez id-ja)
    const newMsg: any = {
      from: user,
      text: trimmedText || "",
      createdAt: Date.now(),
      delivered: false,
      read: false,
    };

    if (imageUrl) {
      newMsg.imageUrl = imageUrl;
    }

    // Pošalji u Firebase
    try {
      console.log("Sending message with data:", newMsg);
      console.log("Thread key:", threadKeyToUse);
      await firebaseStorage.sendChatMessage(threadKeyToUse, newMsg);
      setIsUploadingImage(false);
      // Uspjeh – ukloni optimističnu poruku (stvarna će doći kroz subscription)
      setPendingMsgs((prev) => prev.filter((m) => m.id !== optimisticId));
      // Reset file input
      // Auto-scroll to bottom after sending (newest message at bottom)
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    } catch (error: any) {
      console.error("Error sending message:", error);
      const errorMessage = error?.message || error?.code || "Nepoznata greška";
      console.error("Full error details:", error);
      showToast.error(`Greška pri slanju poruke: ${errorMessage}. Pokušaj ponovo.`);
      setIsUploadingImage(false);
      // Na grešku uklanjamo optimističnu poruku i vraćamo tekst u input
      setPendingMsgs((prev) => prev.filter((m) => m.id !== optimisticId));
      setText(trimmedText);
    }
  }

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast.error("Molimo odaberite sliku.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast.error("Slika je prevelika. Maksimalna veličina je 10MB.");
      return;
    }

    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  function handleCameraClick() {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.click();
    }
  }

  function removeImage() {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  // Get the display name for the active conversation
  const getActiveDisplayName = () => {
    if (withUser) return withUser;
    
    const activeConversation = activeKey
      ? threads.find((c) => c.key === activeKey) || null
      : null;
    
    if (activeConversation && activeConversation.otherLabel) {
      return activeConversation.otherLabel;
    }
    
    if (withUserId) {
      const cachedName = profileCache[withUserId];
      if (cachedName) return cachedName;
    }
    
    // Fallback: try to get name from thread participants
    if (activeKey && user) {
      const activeThread = threads.find((c) => c.key === activeKey);
      if (activeThread) {
        // Parse thread key to get other user ID
        const rest = activeKey.replace("nosiva_chat_", "");
        const lastDoubleUnderscore = rest.lastIndexOf("__");
        let participantsPart = rest;
        if (lastDoubleUnderscore > 0) {
          const afterLastDoubleUnderscore = rest.substring(lastDoubleUnderscore + 2);
          if (afterLastDoubleUnderscore.includes("_")) {
            participantsPart = rest.substring(0, lastDoubleUnderscore + 2);
          }
        }
        const participants = participantsPart.split("__").filter(Boolean);
        const otherId = participants.find((p) => p !== user) || "";
        if (otherId && profileCache[otherId]) {
          return profileCache[otherId];
        }
      }
    }
    
    return user ? "Chat" : "Nisi prijavljena";
  };

  const activeConversation = activeKey
    ? threads.find((c) => c.key === activeKey) || null
    : null;
  const activeDisplayName = getActiveDisplayName();
  const activeOtherLabel =
    activeConversation?.otherLabel && activeConversation.otherLabel !== "General"
      ? activeConversation.otherLabel
      : withUser || (withUserId ? profileCache[withUserId] : undefined) || "";

  // Usklađen thread key za render i učitavanje poruka (koristi isti prioritet kao efekti)
  const threadKeyForRender = activeKey || key;

  // Resolve a user-friendly label for the sender of each message
  const resolveUserLabel = (id: string) => {
    if (!id) return "Nepoznato";
    if (user && id === user) {
      return profileName || "Ti";
    }
    // Try cache first
    if (profileCache[id]) {
      return profileCache[id];
    }
    // Try active conversation
    if (activeOtherLabel && id !== user) {
      return activeOtherLabel;
    }
    // Load profile if not cached
    if (id && !profileCache[id]) {
      getProfileAsync(id).then(async (profile) => {
        const { getDisplayName } = await import("../lib/firebase-storage");
        const displayName = getDisplayName(profile, id);
        profileCache[id] = displayName;
      }).catch(() => {
        // Fallback on error
        profileCache[id] = `Korisnik ${id.substring(0, 8)}...`;
      });
    }
    return profileCache[id] || `Korisnik ${id.substring(0, 8)}...`;
  };

  return (
    <RequireAuth>
      <Header />
      <div
        className="bg-[var(--bg)] flex flex-col chat-page mb-6 sm:mb-8 md:mb-10"
        style={{
          height: `calc(100vh - ${headerHeight}px)`,
        }}
      >
        <div className="flex-1 flex flex-col md:flex-row max-w-7xl mx-auto w-full min-h-0">
          {/* Sidebar with conversations - LIJEVO - VIDLJIV NA MOBILNOM KADA showList=true */}
          <aside
            className={`${isMobile ? (showList ? "flex" : "hidden") : "flex"} md:flex bg-[var(--bg)] border-r border-[var(--border)] flex-col w-full md:w-80 absolute md:static inset-0 md:inset-auto z-20 md:z-auto transition-transform duration-200`}
            style={
              isMobile
                ? {
                    height: "100%",
                  }
                : {}
            }
          >
          <div className="p-3 md:p-4 border-b border-[var(--border)] flex items-center flex-shrink-0">
            <h2 className="text-base md:text-lg font-bold text-[var(--text)]">Razgovori</h2>
          </div>

            <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              {(!user || threads.length === 0) ? (
                <div className="flex items-center justify-center min-h-full px-4 py-12 sm:py-16 pb-20 sm:pb-12">
                  <div className="text-center w-full">
                    <div className="mb-4">
                      <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-[var(--accent)]/10 flex items-center justify-center mb-3 sm:mb-4">
                        <svg
                          className="h-7 w-7 sm:h-8 sm:w-8 text-[var(--accent)]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-[var(--text)] mb-1 sm:mb-2">
                      {user ? "Još nemaš razgovora" : "Prijavi se"}
                    </h3>
                    <p className="text-xs sm:text-sm text-[var(--muted)] mb-4 sm:mb-6">
                      {user
                        ? "Pronađi korisnika i pošalji prvu poruku."
                        : "Prijavi se da započneš razgovor."}
                    </p>
                    {user && (
                      <button
                        onClick={() => router.push("/users")}
                        className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-[var(--accent)] text-white text-xs sm:text-sm font-semibold hover:opacity-90 transition shadow-sm hover:shadow-md"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM20 8v6M23 11h-6" />
                        </svg>
                        Započni novi razgovor
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`space-y-1.5 px-2 pb-2 ${isMobile ? 'pt-8' : 'pt-4'}`}>
                  {threads.map((c, index) => {
                    const isActive = c.key === activeKey;
                    const displayName = c.otherLabel || "Chat";
                    const hasTimestamp = c.lastTs && c.lastTs > 0;
                    const lastDateObj = hasTimestamp ? new Date(c.lastTs) : null;
                    const lastTimeStr = lastDateObj
                      ? lastDateObj.toLocaleTimeString("bs-BA", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "";
                    const lastDateStr = lastDateObj
                      ? `${lastDateObj.getDate().toString().padStart(2, "0")}.${(lastDateObj.getMonth() + 1)
                          .toString()
                          .padStart(2, "0")}.${lastDateObj.getFullYear()}`
                      : "";
                    // Parse thread key to get other user ID
                    const rest = c.key.replace("nosiva_chat_", "");
                    const lastDoubleUnderscore = rest.lastIndexOf("__");
                    let participantsPart = rest;
                    if (lastDoubleUnderscore > 0) {
                      const afterLastDoubleUnderscore = rest.substring(lastDoubleUnderscore + 2);
                      if (afterLastDoubleUnderscore.includes("_")) {
                        participantsPart = rest.substring(0, lastDoubleUnderscore + 2);
                      }
                    }
                    const participants = participantsPart.split("__").filter(Boolean);
                    const otherId = participants.find((p) => p !== user) || "";
                    return (
                      <div
                        key={c.key}
                        className={`relative group ${index === 0 ? 'mt-3' : ''}`}
                      >
                        <button
                          type="button"
                          onClick={async () => {
                          // Find the thread in firebaseThreads to get participants
                          // We need to get the actual thread data to extract otherId properly
                          const activeThread = threads.find(t => t.key === c.key);
                          if (!activeThread) return;

                          // Parse thread key to get other user ID and listing
                          const rest = c.key.replace("nosiva_chat_", "");
                          // Handle listing suffix: userId1__userId2_listingId
                          const lastDoubleUnderscore = rest.lastIndexOf("__");
                          let participantsPart = rest;
                          let listingId: string | null = null;
                          if (lastDoubleUnderscore > 0) {
                            // Check if there's a listing suffix (has _ after last __)
                            const afterLastDoubleUnderscore = rest.substring(lastDoubleUnderscore + 2);
                            if (afterLastDoubleUnderscore.includes("_")) {
                              // Has listing suffix, extract listing ID and participants part
                              const parts = afterLastDoubleUnderscore.split("_");
                              listingId = parts[0] || null; // First part after __ is the listing ID
                              participantsPart = rest.substring(0, lastDoubleUnderscore + 2);
                            }
                          }
                          const participants = participantsPart.split("__").filter(Boolean);
                          const otherId = participants.find((p) => p !== user) || "";

                          // Set active key FIRST - this triggers message loading immediately
                          setActiveKey(c.key);

                          // Navigate to thread with proper params (including listing if present)
                          const search = new URLSearchParams();
                          const otherName = c.otherLabel || profileCache[otherId] || "";
                          
                          if (otherName) {
                            search.set("with", otherName);
                          }
                          if (otherId && otherId !== "general") {
                            search.set("withId", otherId);
                          }
                          if (listingId) {
                            search.set("listing", listingId);
                          }

                          // Mark thread as read immediately when user clicks on it
                          firebaseStorage.markThreadAsRead(c.key).catch(() => {});

                          // Use replace instead of push to avoid adding to history
                          router.replace(`/chat?${search.toString()}`);
                          if (isMobile) {
                            setShowList(false);
                          }

                          // Mark thread as read ONLY when user clicks on thread
                          // Badge STOJI dok se poruka ne pročita - poziva se SAMO na klik
                          if (user && c.key) {
                            // Delay da korisnik vidi poruke prije nego što se badge ukloni
                            setTimeout(async () => {
                              try {
                                console.log("📖 Marking thread as read after user click:", c.key);
                                await firebaseStorage.markThreadAsRead(c.key);
                                console.log("✅ Thread marked as read successfully");
                              } catch (error) {
                                console.error("❌ Error marking thread as read:", error);
                              }
                            }, 2000); // 2 sekunde delay - badge STOJI dok se poruka ne pročita
                          }
                        }}
                        className={`w-full text-left rounded-lg px-3 py-3 transition-all flex items-start gap-3 ${
                          isActive
                            ? "bg-[var(--accent)] text-white shadow-sm"
                            : !isActive && c.unreadCount !== undefined && c.unreadCount > 0
                            ? "bg-red-500/10 hover:bg-red-500/15 border border-red-500/20"
                            : "bg-[var(--card)] hover:bg-[var(--bg)] border border-[var(--border)]"
                        }`}
                      >
                        <Avatar
                          userId={otherId || undefined}
                          displayName={displayName}
                          size="lg"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium text-sm truncate">
                              {displayName}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {hasTimestamp && (
                                <span className={`text-[10px] ${isActive ? "text-white/70" : "text-[var(--muted)]"}`}>
                                  {lastTimeStr}
                                  {lastDateStr && (
                                    <span className="ml-1 opacity-80">
                                      {lastDateStr}
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={`text-xs truncate mt-0.5 ${isActive ? "text-white/80" : "text-[var(--muted)]"}`}>
                            {c.lastText ? (c.lastText.length > 50 ? c.lastText.substring(0, 50) + "..." : c.lastText) : "Nema poruka"}
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (confirm("Da li želiš obrisati ovaj razgovor?")) {
                            try {
                              await firebaseStorage.deleteChatThread(c.key);
                              // If the currently open conversation is the one being deleted,
                              // clear it from the UI so the user sees an empty chat state.
                              if (activeKey === c.key) {
                                setActiveKey(null);
                                setMsgs([]);
                                router.push("/chat");
                              }
                            } catch (error) {
                              console.error("Error deleting chat:", error);
                              showToast.error("Greška pri brisanju razgovora.");
                            }
                          }
                        }}
                        className={`absolute right-2 top-8 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 z-10`}
                        title="Obriši razgovor"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-600">
                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>

          {/* Main chat window - DESNO */}
          <div className={`flex-1 flex flex-col bg-[var(--card)] max-w-3xl mx-auto w-full min-h-0 ${isMobile && showList ? 'hidden' : 'flex'}`}>
            {/* Active conversation header - always sticky unutar chat prozora.
                Cijeli chat je "kutija" od headera do footera, a ovo ostaje na vrhu dok se poruke skrolaju. */}
            <div
              className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 md:py-4 border-b border-[var(--border)] bg-[var(--bg)] sticky top-0 z-[90]"
            >
              {/* Nazad button - na mobilnom vraća na listu, na desktopu ide na marketplace */}
              {isMobile ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowList(true);
                    setActiveKey(null);
                    router.push("/chat");
                  }}
                  className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full border border-[var(--border)] bg-[var(--card)] flex items-center justify-center hover:bg-[var(--bg)] transition flex-shrink-0"
                  aria-label="Nazad na listu"
                  title="Nazad na listu"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sm:w-4 sm:h-4">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    router.push("/");
                  }}
                  className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full border border-[var(--border)] bg-[var(--card)] flex items-center justify-center hover:bg-[var(--bg)] transition flex-shrink-0"
                  aria-label="Nazad na marketplace"
                  title="Nazad na marketplace"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sm:w-4 sm:h-4">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              
              {activeKey ? (
                <button
                  type="button"
                  onClick={() => {
                    // Get other user ID from active thread
                    if (activeKey && user) {
                      const activeThread = threads.find((t) => t.key === activeKey);
                      if (activeThread?.thread) {
                        const otherId = activeThread.thread.participants.find((p) => p !== user);
                        if (otherId) {
                          router.push(`/users/${otherId}`);
                          return;
                        }
                      }
                      if (withUserId) {
                        router.push(`/users/${withUserId}`);
                      }
                    }
                  }}
                  className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition"
                >
                  {(() => {
                    // Get other user ID from active thread
                    let otherUserId: string | undefined = undefined;
                    if (activeKey && user) {
                      const activeThread = threads.find((t) => t.key === activeKey);
                      if (activeThread?.thread) {
                        otherUserId = activeThread.thread.participants.find((p) => p !== user);
                      } else if (withUserId) {
                        otherUserId = withUserId;
                      }
                    } else if (withUserId) {
                      otherUserId = withUserId;
                    }
                    
                    return (
                      <Avatar
                        userId={otherUserId}
                        displayName={activeDisplayName}
                        size="lg"
                        className="h-8 w-8 sm:h-9 sm:w-9 md:h-11 md:w-11"
                      />
                    );
                  })()}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm sm:text-base font-bold text-[var(--text)] truncate">
                      {activeDisplayName}
                    </div>
                  </div>
                </button>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <h2 className="text-sm sm:text-base md:text-lg font-bold text-[var(--text)]">Chat</h2>
                </div>
              )}
            </div>

            {/* Messages area - scroll unutar chata, najnovije poruke pri dnu */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 flex flex-col justify-end space-y-2 sm:space-y-3 md:space-y-4 min-h-0 pb-2 sm:pb-2 relative" 
              onClick={() => setSelectedMessage(null)}
              onScroll={handleScroll}
              style={{ 
                scrollBehavior: 'auto', 
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {showNewMessagesPill && (
                <div className="sticky top-4 z-20 flex justify-center mb-2">
                  <button
                    onClick={() => scrollToBottom()}
                    className="px-4 py-2 bg-[var(--accent)] text-white rounded-full text-sm font-medium shadow-lg hover:opacity-90 transition"
                  >
                    Nove poruke ↑
                  </button>
                </div>
              )}
              {isLoadingOlder && (
                <div className="text-center py-2 text-sm text-[var(--muted)] sticky bottom-4">
                  Učitavanje starijih poruka...
                </div>
              )}
              {!threadKeyForRender || !user ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center py-8 sm:py-12 px-4">
                    <svg
                      className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-[var(--muted)]/50 mb-3 sm:mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <h3 className="text-base sm:text-lg font-semibold text-[var(--text)] mb-1 sm:mb-2">
                      {!user ? "Prijavi se da započneš razgovor" : "Izaberi razgovor"}
                    </h3>
                    <p className="text-xs sm:text-sm text-[var(--muted)] mb-3 sm:mb-4">
                      {!user 
                        ? "Prijavi se da možeš slati poruke korisnicima."
                        : "Izaberi razgovor iz liste ili započni novi sa korisnikom."}
                    </p>
                  </div>
                </div>
              ) : msgs.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-16 w-16 text-[var(--muted)]/50 mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <h3 className="text-lg font-semibold text-[var(--text)] mb-2">
                      Još nema poruka
                    </h3>
                    <p className="text-sm text-[var(--muted)]">
                      Pošalji prvu poruku da započneš razgovor!
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Messages in ASC order (oldest first, newest at bottom), uključujući optimistične poruke */}
                  {combinedMsgs.map((m) => {
                    const isOwn = m.from === user;
                    const authorLabel = resolveUserLabel(m.from);
                    const deletedForEveryone = (m as any).deletedForEveryone || false;
                    const text = deletedForEveryone ? "This message was deleted" : m.text;
                    const isDeleted = deletedForEveryone;
                    const isPending = (m as any).id?.toString().startsWith("pending-");
                    
                    return (
                      <div
                        key={m.id}
                        className={`flex gap-2 sm:gap-3 ${isOwn ? "flex-row-reverse" : ""} relative`}
                      >
                        <Avatar
                          userId={m.from}
                          displayName={authorLabel}
                          size="md"
                          className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0"
                        />
                        <div className={`flex flex-col gap-0.5 sm:gap-1 max-w-[75%] sm:max-w-[60%] md:max-w-[65%] ${isOwn ? "items-end" : "items-start"} relative`}>
                          <div 
                            className={`px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl shadow-sm cursor-pointer hover:opacity-90 transition ${
                              isOwn
                                ? "bg-[var(--accent)] text-white rounded-br-sm"
                                : "bg-[var(--card)] text-[var(--text)] border border-[var(--border)] rounded-bl-sm"
                            } ${isDeleted ? "opacity-60 italic" : ""}`}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setSelectedMessage(m.id);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedMessage === m.id) {
                                setSelectedMessage(null);
                              }
                            }}
                          >
                            {m.imageUrl && (
                              <div className="mb-2 rounded-lg overflow-hidden">
                                <img
                                  src={m.imageUrl}
                                  alt="Chat image"
                                  className="max-w-full max-h-64 object-contain rounded-lg"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(m.imageUrl, '_blank');
                                  }}
                                />
                              </div>
                            )}
                            {text && (
                              <>
                                {isOfferMessage(text) ? (
                                  renderOfferMessage(text, router, isOwn)
                                ) : (
                                  <div className="text-xs sm:text-sm whitespace-pre-wrap break-words leading-relaxed">
                                    {renderMessageText(text, router)}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {selectedMessage === m.id && (
                            <div className={`absolute ${isOwn ? "right-0" : "left-0"} top-full mt-1 bg-[var(--card)] border border-[var(--border)] rounded-lg shadow-lg p-2 z-50 flex flex-col gap-1 min-w-[150px]`}>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const threadKeyToUse = activeKey || key;
                                  if (threadKeyToUse) {
                                    try {
                                      await firebaseStorage.deleteMessageForMe(threadKeyToUse, m.id);
                                      setSelectedMessage(null);
                                    } catch (error) {
                                      console.error("Error deleting message:", error);
                                      showToast.error("Greška pri brisanju poruke.");
                                    }
                                  }
                                }}
                                className="text-sm px-3 py-1.5 text-left hover:bg-[var(--bg)] rounded text-[var(--text)]"
                              >
                                Obriši za mene
                              </button>
                              {isOwn && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const threadKeyToUse = activeKey || key;
                                    if (threadKeyToUse) {
                                      try {
                                        await firebaseStorage.deleteMessageForEveryone(threadKeyToUse, m.id);
                                        setSelectedMessage(null);
                                      } catch (error) {
                                        console.error("Error deleting message:", error);
                                        showToast.error("Greška pri brisanju poruke.");
                                      }
                                    }
                                  }}
                                  className="text-sm px-3 py-1.5 text-left hover:bg-[var(--bg)] rounded text-red-600"
                                >
                                  Obriši za sve
                                </button>
                              )}
                            </div>
                          )}
                          <div className={`text-[10px] px-2 flex items-center gap-1.5 ${isOwn ? "text-white/60" : "text-[var(--muted)]"}`}>
                            <span className="whitespace-nowrap">
                              {new Date(m.createdAt).toLocaleTimeString("bs-BA", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            {isOwn && (
                              <span className="flex items-center gap-0.5">
                                {isPending ? (
                                  <span title="Šalje se..." className="text-white/70 text-[10px]">●●</span>
                                ) : m.delivered === true ? (
                                  m.read === true ? (
                                    <span title="Pročitano" className="flex items-center">
                                      {/* Dva plava checkmarka - READ */}
                                      <svg width="14" height="10" viewBox="0 0 16 11" fill="none" className="text-blue-500 dark:text-blue-400">
                                        <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z" fill="currentColor"/>
                                      </svg>
                                    </span>
                                  ) : (
                                    <span title="Dostavljeno" className="flex items-center">
                                      {/* Jedan bordo checkmark - DELIVERED */}
                                      <svg width="12" height="9" viewBox="0 0 12 9" fill="none" className="text-[#7b1f2a]">
                                        <path d="M11.01 2.316l-.478-.372a.365.365 0 0 0-.51.063L6.666 6.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l4.272-5.548a.366.366 0 0 0-.063-.51z" fill="currentColor"/>
                                      </svg>
                                    </span>
                                  )
                                ) : (
                                  <span title="Šalje se..." className="text-white/50 text-[10px]">○</span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input area - sticky bottom (WhatsApp pattern), bez dodatnog unutarnjeg paddinga pri dnu */}
            {activeKey && (
              <div className="border-t border-[var(--border)] pt-2 sm:pt-3 md:pt-4 pb-0 sm:pb-0 md:pb-0 px-2 sm:px-3 md:px-4 bg-[var(--bg)] sticky bottom-0 z-10">
                {/* Image preview */}
                {imagePreview && (
                  <div className="mb-2 relative">
                    <div className="relative inline-block max-w-[200px] sm:max-w-[300px]">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="rounded-lg max-w-full max-h-48 object-contain border border-[var(--border)]"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition"
                        aria-label="Ukloni sliku"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="flex gap-1.5 sm:gap-2 items-end"
                >
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  
                  {/* Attachment button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full border border-[var(--border)] bg-[var(--card)] flex items-center justify-center hover:bg-[var(--bg)] transition flex-shrink-0"
                    aria-label="Dodaj sliku"
                    title="Dodaj sliku"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                  
                  {/* Camera button */}
                  <button
                    type="button"
                    onClick={handleCameraClick}
                    className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full border border-[var(--border)] bg-[var(--card)] flex items-center justify-center hover:bg-[var(--bg)] transition flex-shrink-0"
                    aria-label="Kamera"
                    title="Kamera"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </button>
                  
                  <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Unesi poruku..."
                    className="flex-1 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--card)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 text-base sm:text-sm"
                    style={{ fontSize: isMobile ? '16px' : undefined }}
                  />
                  <button
                    type="submit"
                    disabled={(!text.trim() && !selectedImage) || isUploadingImage}
                    className="px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-lg sm:rounded-xl bg-[var(--accent)] text-white text-xs sm:text-sm font-semibold hover:bg-[var(--accent)]/90 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                  >
                    {isUploadingImage ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="hidden sm:inline">Šalje se...</span>
                      </>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Pošalji</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sm:hidden">
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </RequireAuth>
  );
}

